import { ApiError, request, withQuery } from './client';
import type {
  AnnexForSaleResult,
  AnnexMode,
  ContractPendingSale,
  ContractAnnexRow,
  ContractCompanyRow,
  ContractFieldsInput,
  ContractInput,
  ContractPreviewResponse,
  ContractRow,
  ContractSourceOption,
  ContractTemplateRow,
  ContractTemplateVersionView,
} from '@kassa/shared';

const DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// Word faylı xam gövdə kimi (multipart yox); növü həmişə docx — brauzer bəzən boş type verir
async function uploadDocx<T>(path: string, file: File, name?: string): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': DOCX, 'X-File-Name': encodeURIComponent(name ?? file.name.replace(/\.docx$/i, '')) },
    body: file,
  });
  const data = (await res.json().catch(() => ({}))) as { message?: string | string[]; code?: string };
  if (!res.ok) {
    const message = Array.isArray(data.message) ? data.message.join(', ') : data.message;
    throw new ApiError(res.status, message ?? 'Xəta baş verdi', data.code);
  }
  return data as T;
}

// Müqavilələr (KT-82). İcazə: contract.manage, modul: contracts.
export const contractsApi = {
  contractSources: () => request<ContractSourceOption[]>('GET', '/api/contracts/sources'),
  contractTemplates: () => request<ContractTemplateRow[]>('GET', '/api/contracts/templates'),
  contractTemplateCreate: (file: File, name: string, kind: 'main' | 'annex') =>
    uploadDocx<ContractTemplateVersionView>(`/api/contracts/templates?kind=${kind}`, file, name),
  contractTemplateUpload: (templateId: string, file: File) =>
    uploadDocx<ContractTemplateVersionView>(`/api/contracts/templates/${encodeURIComponent(templateId)}/versions`, file),
  contractVersion: (id: string) => request<ContractTemplateVersionView>('GET', `/api/contracts/versions/${encodeURIComponent(id)}`),
  contractVersionFields: (id: string, input: ContractFieldsInput) =>
    request<ContractTemplateVersionView>('POST', `/api/contracts/versions/${encodeURIComponent(id)}/fields`, input),
  contractMakeField: (id: string, text: string, tag: string) =>
    request<ContractTemplateVersionView>('POST', `/api/contracts/versions/${encodeURIComponent(id)}/make-field`, { text, tag }),
  contractCompanies: (q: string) => request<ContractCompanyRow[]>('GET', withQuery('/api/contracts/companies', { q })),
  contractAnnexMode: (counterpartyId: string, mode: AnnexMode) =>
    request<void>('POST', `/api/contracts/companies/${encodeURIComponent(counterpartyId)}/annex-mode`, { mode }),
  contractAnnexes: (contractId: string) => request<ContractAnnexRow[]>('GET', `/api/contracts/${encodeURIComponent(contractId)}/annexes`),
  contracts: (counterpartyId?: string) => request<ContractRow[]>('GET', withQuery('/api/contracts', { counterpartyId })),
  contractPreview: (input: ContractInput) => request<ContractPreviewResponse>('POST', '/api/contracts/preview', input),
  contractCreate: (input: ContractInput) => request<ContractRow>('POST', '/api/contracts', input),
  contractPendingSales: (contractId: string) => request<ContractPendingSale[]>('GET', `/api/contracts/${encodeURIComponent(contractId)}/pending-sales`),
  contractAnnexCreate: (contractId: string, docId: string) => request<ContractAnnexRow>('POST', `/api/contracts/${encodeURIComponent(contractId)}/annexes`, { docId }),
  contractAnnexCancel: (annexId: string) => request<void>('POST', `/api/contracts/annexes/${encodeURIComponent(annexId)}/cancel`),
  annexForSale: (docId: string, confirm = false) => request<AnnexForSaleResult>('POST', '/api/contracts/annex-for-sale', { docId, confirm }),
};

export const contractFileUrl = (id: string) => `/api/contracts/${encodeURIComponent(id)}/file`;
export const annexFileUrl = (id: string) => `/api/contracts/annexes/${encodeURIComponent(id)}/file`;
export const contractVersionFileUrl = (id: string) => `/api/contracts/versions/${encodeURIComponent(id)}/file`;
